import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { CookieService } from '../cookie.service';
import * as AppConstant from '../../app.string';
import { environment } from '@env/environment';

export interface StorageConfig {
  enableLocalStorage: boolean;
  enableSessionStorage: boolean;
  enableCookieStorage: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly storageChangeSubject = new BehaviorSubject<{key: string, value: any, type: 'local' | 'session' | 'cookie'}>(null);
  public readonly storageChange$ = this.storageChangeSubject.asObservable();

  private config: StorageConfig = {
    enableLocalStorage: true,
    enableSessionStorage: true,
    enableCookieStorage: true
  };

  constructor(private cookie: CookieService) {
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    if (this.config.enableLocalStorage) {
      this.retrieveDefaultSettings();
    }
  }

  private retrieveDefaultSettings(): void {
    const defaultSettings = [...AppConstant.DEFAULT_STORAGE_SETTINGS];
    
    defaultSettings.forEach(setting => {
      const value = this.getLocalStorage(AppConstant[setting]);
      if (value) {
        // Process default settings
        this.processDefaultSetting(setting, value);
      }
    });
  }

  private processDefaultSetting(setting: string, value: any): void {
    // Implementation for processing default settings
    // Process based on setting name since the constants may not exist
    switch (setting) {
      case 'DEFAULT_LANGUAGE':
        // Handle language setting
        break;
      case 'DEFAULT_DIRECTION':
        // Handle direction setting
        break;
      // Add other cases as needed
    }
  }

  /**
   * Local Storage Methods
   */
  public setLocalStorage(key: string, value: any): void {
    if (!this.config.enableLocalStorage) return;

    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
      this.storageChangeSubject.next({ key, value, type: 'local' });
    } catch (error) {
      console.error('Error setting localStorage:', error);
    }
  }

  public getLocalStorage(key: string): any {
    if (!this.config.enableLocalStorage) return null;

    try {
      const value = localStorage.getItem(key);
      if (value === null) return null;

      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('Error getting localStorage:', error);
      return null;
    }
  }

  public removeLocalStorage(key: string): void {
    if (!this.config.enableLocalStorage) return;

    try {
      localStorage.removeItem(key);
      this.storageChangeSubject.next({ key, value: null, type: 'local' });
    } catch (error) {
      console.error('Error removing localStorage:', error);
    }
  }

  public clearLocalStorage(): void {
    if (!this.config.enableLocalStorage) return;

    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Session Storage Methods
   */
  public setSessionStorage(key: string, value: any): void {
    if (!this.config.enableSessionStorage) return;

    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      sessionStorage.setItem(key, stringValue);
      this.storageChangeSubject.next({ key, value, type: 'session' });
    } catch (error) {
      console.error('Error setting sessionStorage:', error);
    }
  }

  public getSessionStorage(key: string): any {
    if (!this.config.enableSessionStorage) return null;

    try {
      const value = sessionStorage.getItem(key);
      if (value === null) return null;

      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('Error getting sessionStorage:', error);
      return null;
    }
  }

  public removeSessionStorage(key: string): void {
    if (!this.config.enableSessionStorage) return;

    try {
      sessionStorage.removeItem(key);
      this.storageChangeSubject.next({ key, value: null, type: 'session' });
    } catch (error) {
      console.error('Error removing sessionStorage:', error);
    }
  }

  public clearSessionStorage(): void {
    if (!this.config.enableSessionStorage) return;

    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
  }

  /**
   * Cookie Storage Methods (delegated to CookieService)
   */
  public setCookie(key: string, value: any, options?: any): void {
    if (!this.config.enableCookieStorage) return;
    
    this.cookie.set(key, value, options);
    this.storageChangeSubject.next({ key, value, type: 'cookie' });
  }

  public getCookie(key: string): any {
    if (!this.config.enableCookieStorage) return null;
    
    return this.cookie.getCookie(key);
  }

  public removeCookie(key: string): void {
    if (!this.config.enableCookieStorage) return;
    
    // Remove cookie by setting expiry date in the past
    document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
    this.storageChangeSubject.next({ key, value: null, type: 'cookie' });
  }

  /**
   * Generic Storage Methods
   */
  public setStorage(key: string, value: any, type: 'local' | 'session' | 'cookie' = 'local'): void {
    switch (type) {
      case 'local':
        this.setLocalStorage(key, value);
        break;
      case 'session':
        this.setSessionStorage(key, value);
        break;
      case 'cookie':
        this.setCookie(key, value);
        break;
    }
  }

  public getStorage(key: string, type: 'local' | 'session' | 'cookie' = 'local'): any {
    switch (type) {
      case 'local':
        return this.getLocalStorage(key);
      case 'session':
        return this.getSessionStorage(key);
      case 'cookie':
        return this.getCookie(key);
      default:
        return null;
    }
  }

  public removeStorage(key: string, type: 'local' | 'session' | 'cookie' = 'local'): void {
    switch (type) {
      case 'local':
        this.removeLocalStorage(key);
        break;
      case 'session':
        this.removeSessionStorage(key);
        break;
      case 'cookie':
        this.removeCookie(key);
        break;
    }
  }

  /**
   * Utility Methods
   */
  public isStorageAvailable(type: 'local' | 'session'): boolean {
    try {
      const storage = type === 'local' ? localStorage : sessionStorage;
      const test = '__storage_test__';
      storage.setItem(test, 'test');
      storage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  public getStorageSize(type: 'local' | 'session'): number {
    try {
      const storage = type === 'local' ? localStorage : sessionStorage;
      let size = 0;
      
      for (const key in storage) {
        if (storage.hasOwnProperty(key)) {
          size += storage[key].length + key.length;
        }
      }
      
      return size;
    } catch {
      return 0;
    }
  }

  public setStorageConfig(config: Partial<StorageConfig>): void {
    this.config = { ...this.config, ...config };
  }

  public getStorageConfig(): StorageConfig {
    return { ...this.config };
  }

  /**
   * Backup and restore methods
   */
  public backupStorage(type: 'local' | 'session'): Record<string, any> {
    const storage = type === 'local' ? localStorage : sessionStorage;
    const backup: Record<string, any> = {};
    
    try {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key) {
          backup[key] = storage.getItem(key);
        }
      }
    } catch (error) {
      console.error('Error backing up storage:', error);
    }
    
    return backup;
  }

  public restoreStorage(backup: Record<string, any>, type: 'local' | 'session'): void {
    try {
      Object.entries(backup).forEach(([key, value]) => {
        this.setStorage(key, value, type);
      });
    } catch (error) {
      console.error('Error restoring storage:', error);
    }
  }
}
